import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail } from "lucide-react";
import {
  PHONE_DISPLAY, PHONE_TEL_HREF, WHATSAPP_HREF, BRAND_NAME,
} from "@/lib/contact";
import RequestDialog from "@/components/RequestDialog";
import { ZONES } from "@/lib/zones";
import { SERVICES } from "@/lib/services";
import { track } from "@/lib/analytics";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      role="contentinfo"
      className="relative bg-navy text-white overflow-hidden"
    >
      <div className="absolute inset-0 hero-grid opacity-15" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img
                src="/logo-mark.png"
                alt=""
                width="56"
                height="56"
                className="h-14 w-14 object-contain"
                decoding="async"
              />
              <span className="font-display font-extrabold text-xl">{BRAND_NAME}</span>
            </div>
            <p className="mt-4 text-white/75 max-w-md leading-relaxed">
              Servizio di carroattrezzi e soccorso stradale a Roma, attivo 24 ore su 24, 7 giorni su 7. Recupero auto, traino veicoli, batteria scarica, recupero moto: una telefonata, una squadra pronta a partire in tutta Roma e provincia.
            </p>

            <address className="not-italic mt-6 block">
              <a
                href={PHONE_TEL_HREF}
                onClick={() => track.phoneCall("footer")}
                data-testid="footer-phone-block"
                aria-label={`Numero unico H24: ${PHONE_DISPLAY}`}
                className="inline-flex flex-col rounded-2xl border border-white/15 bg-white/5 px-5 py-4 hover:border-brand-orange hover:bg-brand-orange/15 transition"
              >
                <span className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">Numero unico H24</span>
                <span className="mt-1 font-display font-black text-3xl sm:text-4xl text-white">
                  <Phone className="inline h-6 w-6 mr-2 text-brand-orange" aria-hidden="true" />
                  {PHONE_DISPLAY}
                </span>
              </a>
              <p className="mt-3 text-white/60 text-sm">Roma e provincia · Italia</p>
            </address>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-display font-bold text-lg">Azioni rapide</h4>
            <div className="mt-4 flex flex-col gap-3">
              <a
                href={PHONE_TEL_HREF}
                onClick={() => track.phoneCall("footer_action")}
                data-testid="footer-call-button"
                aria-label={`Chiama ora ${PHONE_DISPLAY}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange text-white px-5 py-3 font-bold hover:brightness-110 transition"
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> Chiama Ora
              </a>
              <a
                href={WHATSAPP_HREF}
                onClick={() => track.whatsapp("footer")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-button"
                aria-label="Contattaci su WhatsApp"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-whatsapp text-white px-5 py-3 font-bold hover:brightness-110 transition"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" /> WhatsApp
              </a>
              <RequestDialog
                triggerClassName="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 border border-white/20 text-white px-5 py-3 font-bold hover:bg-white hover:text-navy transition"
                triggerTestId="footer-request-button"
                triggerIcon={<Mail className="h-4 w-4" aria-hidden="true" />}
                triggerLabel="Richiedi Intervento"
              />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h4 className="font-display font-bold text-lg">Zone</h4>
                <ul className="mt-4 space-y-2 text-white/75 text-sm">
                  {ZONES.map((z) => (
                    <li key={z.slug}>
                      <Link to={`/${z.slug}`} className="hover:text-brand-orange">
                        Carro attrezzi {z.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-display font-bold text-lg">Servizi</h4>
                <ul className="mt-4 space-y-2 text-white/75 text-sm">
                  {SERVICES.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/${s.slug}`} className="hover:text-brand-orange">
                        {s.name}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/carro-attrezzi-incidente-assicurazione-roma"
                      data-testid="footer-insurance-link"
                      className="hover:text-brand-orange"
                    >
                      Incidente + assicurazione
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/recupero-auto-spiaggia-ostia-fregene-terracina"
                      data-testid="footer-beach-link"
                      className="hover:text-brand-orange"
                    >
                      Recupero auto in spiaggia
                    </Link>
                  </li>
                  <li>
                    <a
                      href="https://romarottamazioni.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-rottamazione-link"
                      className="hover:text-brand-orange inline-flex items-center gap-1"
                    >
                      Rottamazione auto Roma ↗
                    </a>
                  </li>
                  <li><Link to="/" className="hover:text-brand-orange">Home</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-white/60 text-xs">
          <p>&copy; {new Date().getFullYear()} {BRAND_NAME}. Tutti i diritti riservati.</p>
          <p>Roma e provincia · Operativi 24 ore su 24, 7 giorni su 7</p>
        </div>
      </div>
    </footer>
  );
}
